// =============================================================================
// DesignBriefCard — system designu per domena (motyw Astro „brand"), sekcja
// pelnej szerokosci w Studio /strony. „Zaprojektuj (AI)" -> brief z promptu
// static_design_brief; kostka -> deterministyczny preset z domeny; edycja palety /
// czcionek / trybu / tekstow hero w miejscu; zapis do static_sites.settings.design.
// Brief jedzie do workera w BuildRequest.design i steruje szablonem brand.
// Uklad: rzad 1 = wskazowka + przyciski; rzad 2 = [podglad hero | paleta, selecty,
// fonty, <details> teksty]; stopka = status + „Zapisz design".
// =============================================================================

import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Dices, Save, Loader2, Paintbrush, AlertTriangle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { StaticSiteRow, StaticSiteSettings } from "@/services/staticSite/types";
import {
  FONT_POOL, DESIGN_MOTIFS, contrastRatio, normalizeDesignBrief, type DesignBrief, type DesignPalette,
} from "@/services/staticSite/designBrief";
import { generateDesignBrief, seedBriefFor } from "@/services/staticSite/designBriefGenerator";
import { StudioSection } from "@/components/static-sites/StudioSection";
import { AiDateBadge } from "@/components/static-sites/AiDateBadge";

interface Props {
  site: StaticSiteRow;
  onSaveSettings: (patch: Partial<StaticSiteSettings>) => Promise<void>;
}

const PALETTE_KEYS: Array<{ key: keyof DesignPalette; label: string }> = [
  { key: "bg", label: "Tło" }, { key: "panel", label: "Panel" }, { key: "ink", label: "Tekst" }, { key: "muted", label: "Tekst 2" },
  { key: "border", label: "Ramki" }, { key: "accent", label: "Akcent" }, { key: "accent2", label: "Akcent 2" }, { key: "accent3", label: "Akcent 3" },
];

const MOTIF_LABEL: Record<string, string> = { network: "sieć węzłów", waves: "fale", grid: "siatka", rings: "pierścienie", blobs: "plamy", diagonal: "ukośne pasy" };
const ROLE_LABEL = { heading: "nagłówki", body: "tekst", mono: "mono" } as const;
const SOURCE_LABEL: Record<DesignBrief["source"], string> = { ai: "AI", seed: "z domeny", manual: "ręcznie" };

export function DesignBriefCard({ site, onSaveSettings }: Props) {
  const { toast } = useToast();
  const [brief, setBrief] = useState<DesignBrief | null>(site.settings?.design || null);
  const [hint, setHint] = useState("");
  const [busy, setBusy] = useState<"ai" | "seed" | "save" | null>(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => { setBrief(site.settings?.design || null); setDirty(false); }, [site.id, site.settings?.design]);

  const warnings = useMemo(() => {
    if (!brief) return [];
    const w: string[] = [];
    const p = brief.palette;
    if (contrastRatio(p.ink, p.bg) < 7) w.push(`Tekst vs tło: kontrast ${contrastRatio(p.ink, p.bg).toFixed(1)}:1 (min 7:1)`);
    if (contrastRatio(p.muted, p.bg) < 4.5) w.push(`Tekst 2 vs tło: kontrast ${contrastRatio(p.muted, p.bg).toFixed(1)}:1 (min 4.5:1)`);
    if (contrastRatio(p.accent, p.bg) < 3) w.push(`Akcent vs tło: kontrast ${contrastRatio(p.accent, p.bg).toFixed(1)}:1 (min 3:1)`);
    return w;
  }, [brief]);

  const update = (patch: (b: DesignBrief) => DesignBrief) => {
    setBrief((b) => (b ? { ...patch(b), source: "manual" } : b));
    setDirty(true);
  };

  const runAi = async () => {
    setBusy("ai");
    try {
      const res = await generateDesignBrief({ site, hint });
      setBrief(res.brief);
      setDirty(true);
      toast({ title: "Brief designu gotowy", description: `${res.brief.mode === "dark" ? "ciemny" : "jasny"} · ${res.brief.fonts.heading} + ${res.brief.fonts.body} · ${MOTIF_LABEL[res.brief.motif]} · $${res.costUsd.toFixed(3)}` });
    } catch (e) {
      toast({ title: "Nie udało się zaprojektować", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    } finally {
      setBusy(null);
    }
  };

  const runSeed = async () => {
    setBusy("seed");
    try { setBrief(await seedBriefFor(site)); setDirty(true); } finally { setBusy(null); }
  };

  const save = async () => {
    if (!brief) return;
    setBusy("save");
    try {
      const seed = await seedBriefFor(site);
      const normalized = normalizeDesignBrief(brief, seed, brief.source);
      await onSaveSettings({ design: normalized });
      setBrief(normalized);
      setDirty(false);
      toast({ title: "Design zapisany", description: "Następny build motywu brand użyje tych ustawień." });
    } catch (e) {
      toast({ title: "Nie udało się zapisać", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    } finally {
      setBusy(null);
    }
  };

  const fontsFor = (role: "heading" | "body" | "mono") => FONT_POOL.filter((f) => f.role.includes(role));

  const hero = brief && (
    <div className="flex h-full min-h-56 flex-col justify-center rounded-md border p-5" style={{ background: brief.palette.bg, color: brief.palette.ink, borderColor: brief.palette.border, borderRadius: brief.radius }}>
      <div className="text-[10px] font-semibold tracking-[0.2em] uppercase" style={{ color: brief.palette.accent2 }}>{brief.hero.eyebrow}</div>
      <div className="mt-1 text-2xl font-bold leading-tight">{brief.hero.headline}</div>
      <div className="mt-1 text-sm" style={{ color: brief.palette.muted }}>{brief.hero.subheadline}</div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className="rounded px-3 py-1 text-xs font-semibold" style={{ background: brief.palette.accent, color: brief.mode === "dark" ? brief.palette.bg : "#fff", borderRadius: brief.radius }}>{brief.hero.cta}</span>
        <span className="rounded border px-2 py-1 text-[10px]" style={{ borderColor: brief.palette.accent3, color: brief.palette.accent3, borderRadius: brief.radius }}>{brief.labels.modules}</span>
        <span className="rounded px-2 py-1 text-[10px]" style={{ background: brief.palette.panel, color: brief.palette.muted, borderRadius: brief.radius }}>{brief.labels.knowledge}</span>
      </div>
      {brief.rationale && <div className="mt-3 text-[11px] italic" style={{ color: brief.palette.muted }}>{brief.rationale}</div>}
    </div>
  );

  const paletteRow = brief && (
    <div className="grid grid-cols-4 gap-2 sm:grid-cols-8">
      {PALETTE_KEYS.map(({ key, label }) => (
        <label key={key} className="flex flex-col items-center gap-1 text-[10px] text-muted-foreground" title={brief.palette[key]}>
          <input type="color" value={brief.palette[key]} onChange={(e) => update((b) => ({ ...b, palette: { ...b.palette, [key]: e.target.value } }))} className="h-8 w-full cursor-pointer rounded border bg-transparent p-0" />
          <span className="truncate">{label}</span>
        </label>
      ))}
    </div>
  );

  const selectsRow = brief && (
    <div className="grid grid-cols-3 gap-2">
      <Select value={brief.mode} onValueChange={(v) => update((b) => ({ ...b, mode: v as DesignBrief["mode"] }))}>
        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="dark" className="text-xs">ciemny</SelectItem><SelectItem value="light" className="text-xs">jasny</SelectItem></SelectContent>
      </Select>
      <Select value={brief.motif} onValueChange={(v) => update((b) => ({ ...b, motif: v as DesignBrief["motif"] }))}>
        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
        <SelectContent>{DESIGN_MOTIFS.map((m) => <SelectItem key={m} value={m} className="text-xs">{MOTIF_LABEL[m]}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={String(brief.radius)} onValueChange={(v) => update((b) => ({ ...b, radius: Number(v) as DesignBrief["radius"] }))}>
        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
        <SelectContent>{[0, 4, 8, 14].map((r) => <SelectItem key={r} value={String(r)} className="text-xs">promień {r}px</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );

  const fontsRow = brief && (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
      {(["heading", "body", "mono"] as const).map((role) => (
        <div key={role} className="flex items-center gap-2">
          <span className="w-14 shrink-0 text-[10px] text-muted-foreground">{ROLE_LABEL[role]}</span>
          <Select value={brief.fonts[role]} onValueChange={(v) => update((b) => ({ ...b, fonts: { ...b.fonts, [role]: v as DesignBrief["fonts"]["heading"] } }))}>
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{fontsFor(role).map((f) => <SelectItem key={f.id} value={f.id} className="text-xs">{f.label} <span className="text-muted-foreground">· {f.vibe}</span></SelectItem>)}</SelectContent>
          </Select>
        </div>
      ))}
    </div>
  );

  const heroFields = brief && (
    <div className="space-y-1.5">
      <Input value={brief.hero.eyebrow} onChange={(e) => update((b) => ({ ...b, hero: { ...b.hero, eyebrow: e.target.value } }))} placeholder="etykieta" className="h-8 text-xs" />
      <Input value={brief.hero.headline} onChange={(e) => update((b) => ({ ...b, hero: { ...b.hero, headline: e.target.value } }))} placeholder="nagłówek" className="h-8 text-xs" />
      <Textarea value={brief.hero.subheadline} onChange={(e) => update((b) => ({ ...b, hero: { ...b.hero, subheadline: e.target.value } }))} placeholder="podtytuł" rows={2} className="text-xs" />
      <Input value={brief.hero.cta} onChange={(e) => update((b) => ({ ...b, hero: { ...b.hero, cta: e.target.value } }))} placeholder="przycisk" className="h-8 text-xs" />
    </div>
  );

  const labelFields = brief && (
    <div className="space-y-1.5">
      {([["modules", "sekcja kategorii"], ["modulesIntro", "opis pod kategoriami"], ["knowledge", "sekcja artykułów"], ["about", "sekcja autora"], ["ctaTitle", "pasek CTA: tytuł"], ["ctaButton", "pasek CTA: przycisk"]] as const).map(([k, ph]) => (
        <Input key={k} value={brief.labels[k]} onChange={(e) => update((b) => ({ ...b, labels: { ...b.labels, [k]: e.target.value } }))} placeholder={ph} className="h-8 text-xs" />
      ))}
    </div>
  );

  return (
    <StudioSection
      icon={Paintbrush}
      title="Design AI"
      badge={brief ? <AiDateBadge label={SOURCE_LABEL[brief.source]} date={brief.generatedAt} /> : undefined}
      help="Paleta, czcionki (hostowane lokalnie, zero Google Fonts), motyw dekoracji i teksty strony głównej. AI dobiera je do tematu mapy, logo i persony. Bez briefu worker losuje preset z domeny."
      footer={brief ? (
        <>
          {!dirty && site.settings?.design && <span className="text-xs text-muted-foreground">zapisane</span>}
          <Button size="sm" onClick={() => void save()} disabled={!dirty || busy !== null}>
            {busy === "save" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Zapisz design
          </Button>
        </>
      ) : undefined}
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Input id="design-hint" value={hint} onChange={(e) => setHint(e.target.value)} placeholder="Wskazówka dla AI, np. ciemno i technicznie; albo: ciepło, jasno, dla rodziców" className="h-8 min-w-56 flex-1 text-xs" />
          <Button size="sm" onClick={() => void runAi()} disabled={busy !== null}>
            {busy === "ai" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />} {brief ? "Zaprojektuj ponownie" : "Zaprojektuj (AI)"}
          </Button>
          <Button size="sm" variant="outline" onClick={() => void runSeed()} disabled={busy !== null} title="Deterministyczny preset z nazwy domeny, bez kosztu">
            {busy === "seed" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Dices className="h-4 w-4" />}
          </Button>
        </div>
        {brief ? (
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="min-w-0">{hero}</div>
            <div className="min-w-0 space-y-3">
              {paletteRow}
              {selectsRow}
              {fontsRow}
              {warnings.length > 0 && (
                <ul className="space-y-0.5 rounded-md border border-amber-500/30 bg-amber-500/5 p-2 text-xs">
                  {warnings.map((w, i) => <li key={i} className="flex items-center gap-1"><AlertTriangle className="h-3 w-3 text-amber-500" /> {w}</li>)}
                </ul>
              )}
              <details>
                <summary className="cursor-pointer text-xs text-muted-foreground">Teksty hero i etykiety sekcji</summary>
                <div className="mt-2 grid gap-3 sm:grid-cols-2">
                  {heroFields}
                  {labelFields}
                </div>
              </details>
            </div>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">Bez briefu worker losuje preset z domeny. Kliknij „Zaprojektuj (AI)” albo kostkę.</p>
        )}
      </div>
    </StudioSection>
  );
}
